import React, { useEffect, useMemo, useRef, useState, useLayoutEffect, useCallback } from "react";
import { useTable, useSortBy, useGlobalFilter, usePagination } from "react-table";
import { gsap } from "gsap";
import ApiURl from "../../controllers/Api";

/**
 * Safely reads a response as JSON.
 * If server returns HTML/PHP errors, it throws a useful error message instead of "Unexpected token <".
 */
async function readJsonSafe(res) {
  const text = await res.text();

  // Try parse JSON
  try {
    const data = JSON.parse(text);
    return { data, rawText: text };
  } catch {
    // Not JSON (likely PHP warning/notice or server error page)
    const preview = text?.slice(0, 400);
    throw new Error(
      `Server did not return JSON (HTTP ${res.status}). Response starts with:\n${preview}`
    );
  }
}

/**
 * PHP-friendly POST helper (works with scripts expecting $_POST).
 */
async function postForm(url, payloadObj) {
  const body = new URLSearchParams();
  Object.entries(payloadObj).forEach(([k, v]) => body.append(k, String(v)));

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" },
    body,
  });

  const { data } = await readJsonSafe(res);
  return { res, data };
}

const ProductList = () => {
  const API_BASE = String(ApiURl || "").replace(/\/$/, "");

  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Edit modal
  const [editProduct, setEditProduct] = useState(null);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newImage, setNewImage] = useState(null);

  // Animation refs
  const rowsWrapRef = useRef(null);
  const modalRef = useRef(null);

  // Fetch products with their sizes
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        // First fetch all products
        const res = await fetch(`${API_BASE}/getProducts.php`);
        const { data } = await readJsonSafe(res);

        if (!data?.success) throw new Error(data?.message || "Failed to fetch products.");

        const products = Array.isArray(data.products) ? data.products : [];
        
        // Then fetch sizes for each product
        const productsWithSizes = await Promise.all(
          products.map(async (product) => {
            try {
              const sizeRes = await fetch(
                `${API_BASE}/getProductSizes.php?product_id=${product.product_id}`
              );
              const sizeData = await readJsonSafe(sizeRes);
              
              if (sizeData.data?.success && Array.isArray(sizeData.data.sizes)) {
                // If product has multiple sizes, create a row for each size
                return sizeData.data.sizes.map(size => ({
                  ...product,
                  size: size.size,
                  price: size.price,
                  is_available: size.is_available ?? product.is_available,
                  size_id: size.id
                }));
              } else {
                // If no sizes found, return product with default values
                return [{
                  ...product,
                  size: "-",
                  price: product.price || 0,
                  is_available: product.is_available || 0
                }];
              }
            } catch (error) {
              console.error(`Error fetching sizes for product ${product.product_id}:`, error);
              return [{
                ...product,
                size: "-",
                price: product.price || 0,
                is_available: product.is_available || 0
              }];
            }
          })
        );

        // Flatten the array of arrays
        setProductList(productsWithSizes.flat());
      } catch (err) {
        console.error(err);
        setError(err.message || "Something went wrong.");
        setProductList([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [API_BASE]);

  // GSAP: animate rows when data changes
  useLayoutEffect(() => {
    if (!rowsWrapRef.current) return;

    const ctx = gsap.context(() => {
      const targets = gsap.utils.toArray(".pl-row");
      if (!targets.length) return;

      gsap.fromTo(
        targets,
        { opacity: 0, y: 8 },
        { opacity: 1, y: 0, duration: 0.35, stagger: 0.02, ease: "power2.out" }
      );
    }, rowsWrapRef);

    return () => ctx.revert();
  }, [productList]);

  // GSAP: modal pop-in
  useLayoutEffect(() => {
    if (!editProduct || !modalRef.current) return;

    const ctx = gsap.context(() => {
      const targets = gsap.utils.toArray(".pl-modal");
      if (!targets.length) return;

      gsap.fromTo(
        targets,
        { opacity: 0, y: 12, scale: 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.25, ease: "power2.out" }
      );
    }, modalRef);

    return () => ctx.revert();
  }, [editProduct]);

  // Delete product
  const handleDelete = useCallback(
    async (productId) => {
      if (!window.confirm("Are you sure you want to delete this product?")) return;

      try {
        const { data } = await postForm(`${API_BASE}/deleteProduct.php`, {
          product_id: productId,
        });

        if (!data?.success) {
          alert(data?.message || "Failed to delete product.");
          return;
        }

        setProductList((prev) =>
          prev.filter((p) => String(p.product_id) !== String(productId))
        );

        alert(data?.message || "Deleted successfully.");
      } catch (err) {
        console.error(err);
        alert(`Delete failed.\n\n${err.message}`);
      }
    },
    [API_BASE]
  );

  // Availability toggle
  const handleAvailabilityToggle = useCallback(
    async (productId, size, available, sizeId) => {
      try {
        const { data } = await postForm(`${API_BASE}/updateProductAvailability.php`, {
          product_id: productId,
          size: size,
          size_id: sizeId || '',
          available: available,
        });

        if (!data?.success) {
          alert(data?.message || "Failed to update availability.");
          return;
        }

        setProductList((prev) =>
          prev.map((p) =>
            String(p.product_id) === String(productId) && String(p.size) === String(size)
              ? { ...p, is_available: available }
              : p
          )
        );
      } catch (err) {
        console.error(err);
        alert(`Availability update failed.\n\n${err.message}`);
      }
    },
    [API_BASE]
  );

  // Edit handlers
  const handleEdit = useCallback((product) => {
    setEditProduct(product);
    setNewName(product?.name || "");
    setNewPrice(String(product?.price ?? ""));
    setNewImage(null);
  }, []);

  const closeModal = useCallback(() => {
    setEditProduct(null);
    setNewName("");
    setNewPrice("");
    setNewImage(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editProduct?.product_id || !editProduct?.size) {
      alert("Invalid product selection.");
      return;
    }
    if (!newName.trim()) {
      alert("Please enter the product name.");
      return;
    }

    const parsedPrice = parseFloat(newPrice);
    if (Number.isNaN(parsedPrice) || parsedPrice <= 0) {
      alert("Please enter a valid price.");
      return;
    }

    const formData = new FormData();
    formData.append("product_id", editProduct.product_id);
    formData.append("name", newName.trim());
    formData.append("price", String(parsedPrice));
    formData.append("size", editProduct.size);
    formData.append("size_id", editProduct.size_id || '');

    if (newImage instanceof File) formData.append("image", newImage);

    try {
      const res = await fetch(`${API_BASE}/updateProductDetails.php`, {
        method: "POST",
        body: formData,
      });

      const { data } = await readJsonSafe(res);

      if (!data?.success) {
        alert(data?.message || "Failed to update product.");
        return;
      }

      setProductList((prev) =>
        prev.map((p) =>
          String(p.product_id) === String(editProduct.product_id) &&
          String(p.size) === String(editProduct.size)
            ? {
                ...p,
                name: newName.trim(),
                price: parsedPrice,
                image: data?.data?.img_path || p.image,
              }
            : p
        )
      );

      alert(data?.message || "Updated successfully.");
      closeModal();
    } catch (err) {
      console.error(err);
      alert(`Update failed.\n\n${err.message}`);
    }
  }, [API_BASE, editProduct, newName, newPrice, newImage, closeModal]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Product",
        accessor: "name",
        Cell: ({ value }) => <div className="font-medium text-[#2B0000]">{value || "-"}</div>,
      },
      {
        Header: "Size",
        accessor: "size",
        Cell: ({ value }) => (
          <span className="inline-flex items-center rounded-full border border-[#d4af37]/30 bg-[#fff7e8] px-2.5 py-1 text-xs font-semibold text-[#6a4b1f]">
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Amount",
        accessor: "price",
        Cell: ({ value }) => <span className="font-semibold text-[#2B0000]">₹{value ?? "-"}</span>,
      },
      {
        Header: "Available",
        accessor: "is_available",
        disableSortBy: true,
        Cell: ({ row }) => {
          const checked = row.original.is_available === 1;
          return (
            <button
              type="button"
              onClick={() =>
                handleAvailabilityToggle(
                  row.original.product_id,
                  row.original.size,
                  checked ? 0 : 1,
                  row.original.size_id
                )
              }
              className={[
                "relative inline-flex h-6 w-11 items-center rounded-full transition",
                checked ? "bg-[#6f8f67]" : "bg-[#cdb9aa]",
              ].join(" ")}
              aria-label="Toggle availability"
              title="Toggle availability"
            >
              <span
                className={[
                  "inline-block h-5 w-5 transform rounded-full bg-white shadow transition",
                  checked ? "translate-x-5" : "translate-x-1",
                ].join(" ")}
              />
            </button>
          );
        },
      },
      {
        Header: "Category",
        accessor: "category_id",
        Cell: ({ value }) => <span className="text-[#5d4b44]">{value ?? "-"}</span>,
      },
      {
        Header: "Created",
        accessor: "created_at",
        Cell: ({ value }) => <span className="text-[#75645d]">{value ?? "-"}</span>,
      },
      {
        Header: "Actions",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleDelete(row.original.product_id)}
              className="rounded-lg border border-red-200 bg-[#fff2ef] px-3 py-1.5 text-sm font-semibold text-[#9d2b25] transition hover:bg-[#9d2b25] hover:text-white active:translate-y-[1px]"
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [handleAvailabilityToggle, handleEdit, handleDelete]
  );

  const tableInstance = useTable(
    {
      columns,
      data: productList,
      initialState: { pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    pageOptions,
    gotoPage,
    setPageSize,
  } = tableInstance;

  const { globalFilter, pageIndex, pageSize } = state;

  if (loading) {
    return (
      <div className="product-list-luxury min-h-screen w-full bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
        <div className="rounded-[22px] border border-[#d9c8b9] bg-[#fffaf5] p-5 shadow-[0_18px_45px_rgba(43,0,0,0.08)]">
          <div className="h-7 w-44 animate-pulse rounded-lg bg-[#eadfd5]" />
          <div className="mt-4 space-y-2">
            <div className="h-11 animate-pulse rounded-xl bg-[#f2e8df]" />
            <div className="h-11 animate-pulse rounded-xl bg-[#f2e8df]" />
            <div className="h-11 animate-pulse rounded-xl bg-[#f2e8df]" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-[22px] border border-red-200 bg-[#fff4f2] p-5 text-red-800 shadow-[0_18px_45px_rgba(43,0,0,0.08)]">
        <div className="font-semibold">Error</div>
        <div className="mt-1 text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="product-list-luxury min-h-screen w-full bg-[#F7EFE7] p-3 font-[Kanit] text-[#2B0000] sm:p-5 lg:p-7">
      {/* Header / Controls */}
      <div className="mb-5 overflow-hidden rounded-[24px] border border-[#d9c8b9] bg-[linear-gradient(135deg,#fffaf5,#f4e9df)] p-5 shadow-[0_18px_45px_rgba(43,0,0,0.08)] sm:p-6">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-serif text-xl font-medium text-[#f6e2b5]">Products</h2>
            <p className="text-sm text-white/60">
              Search, sort, update availability, and manage products.
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <input
                value={globalFilter || ""}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder="Search products..."
                className="w-full rounded-xl border border-[#d9c8b9] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition placeholder:text-[#8d7b73] focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10 sm:w-72"
              />
            </div>

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-xl border border-[#d9c8b9] bg-white px-4 py-3 text-sm text-[#2B0000] shadow-sm outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
            >
              {[10, 20, 30, 50].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-[24px] border border-[#d9c8b9] bg-[#fffaf5] shadow-[0_22px_55px_rgba(43,0,0,0.08)]">
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="w-full min-w-[900px] text-left text-sm">
            <thead className="sticky top-0 z-10 bg-[#efe3d8]">
              {headerGroups.map((headerGroup, i) => (
                <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                  {headerGroup.headers.map((column, j) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={j}
                      className="whitespace-nowrap border-b border-[#d9c8b9] px-4 py-3.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#4a302b]"
                    >
                      <div className="flex items-center gap-2">
                        {column.render("Header")}
                        {column.canSort && (
                          <span className="text-[#9b7d6f]">
                            {column.isSorted ? (column.isSortedDesc ? "▼" : "▲") : "↕"}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody {...getTableBodyProps()} ref={rowsWrapRef}>
              {page.length > 0 ? (
                page.map((row, idx) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      key={considerKey(row, idx)}
                      className="pl-row border-t border-[#eadfd5] transition hover:bg-[#fff5ea]"
                    >
                      {row.cells.map((cell, cidx) => (
                        <td
                          {...cell.getCellProps()}
                          key={cidx}
                          className="whitespace-nowrap px-4 py-4 text-[#4d3d37]"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr className="border-t">
                  <td className="px-4 py-10 text-center text-[#75645d]" colSpan={columns.length}>
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col gap-3 border-t border-[#d9c8b9] bg-[#fbf3ec] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-white/60">
            Page <span className="font-semibold">{pageIndex + 1}</span> of{" "}
            <span className="font-semibold">{pageOptions.length}</span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              className="rounded-xl border border-[#cdb9aa] bg-white px-3.5 py-2 text-sm font-medium text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed] disabled:cursor-not-allowed disabled:opacity-40"
            >
              First
            </button>
            <button
              type="button"
              onClick={previousPage}
              disabled={!canPreviousPage}
              className="rounded-xl border border-[#cdb9aa] bg-white px-3.5 py-2 text-sm font-medium text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Prev
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={!canNextPage}
              className="rounded-xl border border-[#cdb9aa] bg-white px-3.5 py-2 text-sm font-medium text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => gotoPage(pageOptions.length - 1)}
              disabled={!canNextPage}
              className="rounded-xl border border-[#cdb9aa] bg-white px-3.5 py-2 text-sm font-medium text-[#2B0000] transition hover:border-[#c59a4a] hover:bg-[#fff7ed] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editProduct && (
        <div
          ref={modalRef}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-[#2B0000]/35 p-4 backdrop-blur-sm"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="pl-modal w-full max-w-lg overflow-hidden rounded-[24px] border border-[#d4af37]/25 bg-[#fffaf5] shadow-[0_30px_90px_rgba(43,0,0,0.24)]">
            <div className="flex items-center justify-between border-b border-[#d4af37]/20 bg-[linear-gradient(135deg,#5a2020,#2B0000)] px-5 py-4 text-white">
              <div>
                <div className="font-serif text-xl font-medium text-[#f6e2b5]">Edit Product</div>
                <div className="text-sm text-white/60">
                  ID: {editProduct.product_id} • Size: {editProduct.size}
                </div>
              </div>
              <button
                onClick={closeModal}
                className="rounded-lg border border-white/20 px-3 py-1.5 text-sm text-white/80 transition hover:bg-white/10"
                type="button"
              >
                Close
              </button>
            </div>

            <div className="space-y-5 px-5 py-5">
              <div>
                <label className="text-sm font-semibold text-[#4b352f]">Product Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d9c8b9] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#4b352f]">Product Price</label>
                <input
                  type="number"
                  value={newPrice}
                  onChange={(e) => setNewPrice(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-[#d9c8b9] bg-white px-4 py-3 text-sm text-[#2B0000] outline-none transition focus:border-[#c59a4a] focus:ring-4 focus:ring-[#d4af37]/10"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#4b352f]">Product Image</label>
                <input
                  type="file"
                  onChange={(e) => setNewImage(e.target.files?.[0] || null)}
                  className="mt-2 w-full rounded-xl border border-[#d9c8b9] bg-white px-4 py-3 text-sm text-[#2B0000]"
                />
                <div className="mt-2 text-xs text-[#806f68]">
                  Upload only if you want to change the image.
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-[#d9c8b9] bg-[#fbf3ec] px-5 py-4">
              <button
                onClick={closeModal}
                className="rounded-xl border border-[#cdb9aa] bg-white px-4 py-2 text-sm font-semibold text-[#2B0000] transition hover:bg-[#fff7ed]"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="rounded-xl bg-[#2B0000] px-4 py-2 text-sm font-semibold text-[#f6e2b5] shadow-md transition hover:bg-[#4a090b] active:translate-y-[1px]"
                type="button"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .product-list-luxury {
          background:
            radial-gradient(circle at 8% 0%, rgba(212,175,55,.10), transparent 28%),
            radial-gradient(circle at 95% 90%, rgba(43,0,0,.045), transparent 30%),
            #F7EFE7;
        }

        .product-list-luxury input,
        .product-list-luxury select,
        .product-list-luxury button {
          font: inherit;
        }

        .product-list-luxury table {
          border-collapse: separate;
          border-spacing: 0;
        }

        .product-list-luxury tbody tr:last-child td {
          border-bottom: 0;
        }

        .product-list-luxury ::selection {
          color: #fff8ed;
          background: #7a3131;
        }

        .product-list-luxury * {
          scrollbar-width: thin;
          scrollbar-color: rgba(122,49,49,.34) rgba(212,175,55,.07);
        }

        .product-list-luxury *::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .product-list-luxury *::-webkit-scrollbar-track {
          background: rgba(212,175,55,.07);
        }

        .product-list-luxury *::-webkit-scrollbar-thumb {
          border: 2px solid transparent;
          border-radius: 999px;
          background: rgba(122,49,49,.34);
          background-clip: padding-box;
        }

        @media (max-width: 640px) {
          .product-list-luxury {
            padding: 10px;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .product-list-luxury *,
          .product-list-luxury *::before,
          .product-list-luxury *::after {
            transition-duration: .01ms !important;
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
};

// Better stable key if product_id is present
function considerKey(row, fallbackIndex) {
  const original = row?.original;
  const pid = original?.product_id;
  const size = original?.size;
  return pid ? `${pid}-${size || ""}` : `row-${fallbackIndex}`;
}

export default ProductList;